import { PublicNavigationLink as Link } from "./PublicNavigationLink";
import { useTranslation } from "react-i18next";
import { usePlatform } from "@/components/platform/PlatformProvider";
import { DsIcon } from "@/components/ui/ds-icons";
import { useFeatureFlag } from "@/hooks/useFeatureFlags";
import { openConsentSettings } from "@/lib/analytics/consent";
import { FooterAppDownloads } from "./FooterAppDownloads";
import { useAppTranslation } from "@/i18n/useAppTranslation";
import { PUBLIC_NAVIGATION_GROUPS } from "@/components/layout/navigation";
import type { NavigationLink } from "@/components/layout/navigation";
import footerLogoBac from "@/assets/logo-bookaclassic-inverse.png";
import footerLogoDs from "@/assets/logo-driveshare-inverse.png";

export function Footer() {
  const config = usePlatform();
  const { t } = useAppTranslation();
  const { t: tLegal } = useTranslation("legal");
  const year = new Date().getFullYear();
  const isBac = config.platform === "bac";
  const showIosAppStoreBadge = useFeatureFlag("feature_show_ios_app_store_badge");
  const showAndroidPlayStoreBadge = useFeatureFlag("feature_show_android_play_store_badge");
  const referralsEnabled = useFeatureFlag("feature_referrals");
  const proOwnerEnabled = useFeatureFlag("feature_pro_owner");

  const navigationGroups = PUBLIC_NAVIGATION_GROUPS
    .map((group) => ({
      ...group,
      links: group.links.filter((link) =>
        link.featureFlag === "feature_pro_owner" ? proOwnerEnabled :
        link.featureFlag === "feature_referrals" ? referralsEnabled : true,
      ),
    }))
    .filter((group) => group.links.length > 0);

  const bottomLinks = [
    { href: "/terms", label: t("footer.terms_short") },
    { href: "/privacy", label: t("footer.privacy_short") },
    { href: "/help/topic/trust-and-safety", label: tLegal("footer_links.policies") },
    { href: "/help/topic/booking-a-car", label: tLegal("footer_links.faq_procedures") },
    { external: true, href: "https://shop.driveshare.com", label: t("footer.shop") },
    // Keep the contact-support destination as a separate action below this
    // list rather than duplicating it in the Help & Company group.
  ];

  const copyrightLine = isBac
    ? tLegal("common.footer_copyright_bac", { year, brand: config.name })
    : tLegal("common.footer_copyright_ds", { year });
const footerLinkClass = "inline-flex min-h-11 min-w-11 items-center text-sm text-white/80 transition-colors hover:text-white";

  return (
    <footer className="bg-primary text-primary-foreground">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 gap-6 sm:gap-8 lg:grid-cols-[1.2fr_repeat(4,minmax(0,1fr))]">
          <div className="col-span-2 lg:col-span-1">
            <div className="flex items-center gap-3">
              <img
                src={
                  config.platform === "driveshare"
                    ? footerLogoDs
                    : footerLogoBac
                }
              alt={config.fullName}
                className="h-10 w-auto"
              />
            </div>
            <p className="mt-4 text-sm text-white/80 max-w-xs">
              {config.tagline || t("footer.default_tagline")}
            </p>

            <FooterAppDownloads ios={showIosAppStoreBadge} android={showAndroidPlayStoreBadge} />

            <div className="mt-6 flex gap-4">
              {config.socialLinks.facebook && (
                <a href={config.socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="touch-44 inline-flex items-center justify-center text-white/60 transition-colors hover:text-white" aria-label="Facebook">
                  <DsIcon name="facebook" className="h-5 w-5" />
                </a>
              )}
              {config.socialLinks.instagram && (
                <a href={config.socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="touch-44 inline-flex items-center justify-center text-white/60 transition-colors hover:text-white" aria-label="Instagram">
                  <DsIcon name="instagram" className="h-5 w-5" />
                </a>
              )}
              {config.socialLinks.tiktok && (
                <a href={config.socialLinks.tiktok} target="_blank" rel="noopener noreferrer" className="touch-44 inline-flex items-center justify-center text-white/60 transition-colors hover:text-white" aria-label="TikTok">
                  <DsIcon name="tiktok" className="h-5 w-5" />
                </a>
              )}
              {config.socialLinks.x && (
                <a href={config.socialLinks.x} target="_blank" rel="noopener noreferrer" className="touch-44 inline-flex items-center justify-center text-white/60 transition-colors hover:text-white" aria-label="X">
                  <DsIcon name="twitter" className="h-5 w-5" />
                </a>
              )}
              {config.socialLinks.youtube && (
                <a href={config.socialLinks.youtube} target="_blank" rel="noopener noreferrer" className="touch-44 inline-flex items-center justify-center text-white/60 transition-colors hover:text-white" aria-label="YouTube">
                  <DsIcon name="youtube" className="h-5 w-5" />
                </a>
              )}
            </div>
          </div>

          {navigationGroups.map((group) => (
            <nav key={group.id} aria-label={t(group.labelKey, group.fallback)}>
              <h3 className="text-base font-semibold text-white">{t(group.labelKey, group.fallback)}</h3>
              <ul className="mt-4 space-y-0 sm:space-y-1">
                {group.links.map((link: NavigationLink) => (
                  <li key={link.href}>
                    {link.external ? (
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={footerLinkClass}
                      >
                        {t(link.labelKey, link.fallback)}
                      </a>
                    ) : (
                      <Link href={link.global ? `~${import.meta.env.BASE_URL}${link.href.slice(1)}` : link.href} className={footerLinkClass}>
                        {t(link.labelKey, link.fallback)}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
            <p className="text-sm text-white/80" data-testid="footer-copyright">
              {copyrightLine}
            </p>
            <div className="flex flex-wrap gap-6">
              {bottomLinks.map((link) =>
                "external" in link && link.external ? (
                  <a
                    key={link.href + link.label}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={footerLinkClass}
                    data-testid="footer-shop-link"
                  >
                    {link.label}
                  </a>
                ) : (
                  <Link key={link.href + link.label} href={link.href} className={footerLinkClass}>
                    {link.label}
                  </Link>
                ),
              )}
              <Link
                href="/help/contact"
                className={footerLinkClass}
                data-testid="footer-help-chat"
              >
                {t("common.contact_support")}
              </Link>
              <button
                type="button"
                onClick={openConsentSettings}
                className={footerLinkClass}
                data-testid="footer-cookie-preferences"
              >
                {t("footer.cookie_preferences")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
